import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CheckIcon,
  Combobox,
  Group,
  Pill,
  PillsInput,
  useCombobox,
} from "@mantine/core";
import { FunctionComponent, useState } from "react";
import { TAGS_TO_ICONS } from "../../utils/amenityTags";

export interface AmenityTagSelectorProps {
  /** Selected amenity tag keys */
  value: string[];
  /** Callback when selection changes */
  onChange: (value: string[]) => void;
  /** Placeholder text when no tags selected */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * Reusable amenity tag multi-select component.
 * Displays amenity tags with icons and allows multi-selection.
 */
export const AmenityTagSelector: FunctionComponent<AmenityTagSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select amenities",
  disabled = false,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [search, setSearch] = useState("");

  const options = Object.entries(TAGS_TO_ICONS)
    .filter(([key]) => key.toLowerCase().includes(search.trim().toLowerCase()))
    .map(([key, tag]) => (
      <Combobox.Option value={key} key={key} active={value.includes(key)}>
        <Group gap="sm">
          <FontAwesomeIcon icon={tag.icon} />
          <span>{tag.tooltip}</span>
          {value.includes(key) ? <CheckIcon size={12} /> : null}
        </Group>
      </Combobox.Option>
    ));

  const handleValueSelect = (val: string) => {
    const newValue = value.includes(val)
      ? value.filter((v) => v !== val)
      : [...value, val];
    onChange(newValue);
  };

  const handleValueRemove = (val: string) => {
    const newValue = value.filter((v) => v !== val);
    onChange(newValue);
  };

  const pills = value.map((item) => (
    <Pill
      key={item}
      withRemoveButton={!disabled}
      onRemove={() => handleValueRemove(item)}
    >
      {TAGS_TO_ICONS[item]?.tooltip || item}
    </Pill>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      disabled={disabled}
      withinPortal
    >
      <Combobox.DropdownTarget>
        <PillsInput
          onClick={() => !disabled && combobox.openDropdown()}
          disabled={disabled}
          pointer
        >
          <Pill.Group>
            {pills}
            <Combobox.EventsTarget>
              <PillsInput.Field
                placeholder={value.length === 0 ? placeholder : undefined}
                value={search}
                onChange={(event) => {
                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onFocus={() => combobox.openDropdown()}
                onBlur={() => {
                  combobox.closeDropdown();
                  setSearch("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && search.length === 0) {
                    event.preventDefault();
                    if (value.length > 0) {
                      handleValueRemove(value[value.length - 1]);
                    }
                  }
                }}
                disabled={disabled}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown style={{ zIndex: 320 }}>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>No amenities found</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
